import { LegacyRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useGetPortTargetCatalogFilterOptionsQuery } from 'app/api/facade';
import { useAppSelector } from 'app/store/store';
import styles from './FilterPanel.module.scss';


import { useTranslation } from 'react-i18next';
import { Icon } from 'app/components/Icon';
import { useFormik } from 'formik';
import { useGetAllValue } from 'app/hooks/useGetAllValue';
import { Button } from '../Button';
import { Input } from '../Input';
import { CheckboxAll } from './CheckboxAll';
import { CheckboxOption } from './CheckboxOption';
import { PortTargetCatalogFilters } from 'app/pages/PortDetails/PortVessels';
import clsx from 'clsx';

interface Props {
  filters?: PortTargetCatalogFilters;
  onClose: () => void;
  innerRef?: LegacyRef<HTMLDivElement>;
  handleFilters: React.Dispatch<React.SetStateAction<PortTargetCatalogFilters | undefined>>;
}

export interface FilterFormData {
  touched: boolean;
  fleetOnly: boolean;
  country: string[];
  type: string[];
  fleetGroup: string[];
  typeAll: boolean;
  countryAll: boolean;
  fleetGroupAll: boolean;
}
export const PortTargetCatalogFilterPanel = ({ onClose, innerRef, filters, handleFilters }: Props) => {
  const { t } = useTranslation();
  const { data, isError, error, ...filterOptionsRequest } = useGetPortTargetCatalogFilterOptionsQuery();
  const uiConfig = useAppSelector((state) => state.userSlice.uiConfig);

  const allTypes = useGetAllValue(data?.filter?.type?.values);
  const allCountries = useGetAllValue(data?.filter?.country?.values);
  const allFleetGroups = useGetAllValue(data?.filter?.fleetGroup?.values);

  const [countryList, setContryList] = useState(data?.filter?.country?.values);
  const [inputValue, setInputValue] = useState<string>('');

  useEffect(() => {
    filterOptionsRequest.refetch();
  }, []);

  useEffect(() => {
    if (data?.filter?.country?.values) {
      setContryList(data?.filter?.country?.values);
    }
  }, [data]);

  const initialValues = useMemo(() => {
    return {
      touched: false,
      fleetOnly: uiConfig?.viewportFleetOnly || false,
      type: allTypes,
      country: allCountries,
      fleetGroup: allFleetGroups,
      statusAll: true,
      typeAll: true,
      countryAll: true,
      sourceAll: true,
      fleetGroupAll: true,
    };
  }, [allCountries, allTypes, allFleetGroups, uiConfig]);

  const initialFormValue = useMemo(() => {
    if (filters) {
      return {
        touched: filters.touched || false,
        fleetOnly: filters.fleetOnly || uiConfig?.viewportFleetOnly || false,
        type: filters.type?.map((e) => String(e)) || [],
        country: filters.country?.map((e) => String(e)) || [],
        fleetGroup: filters.fleetGroup?.map((e) => String(e)) || [],
        typeAll: filters.typeAll,
        countryAll: filters.countryAll,
        fleetGroupAll: filters.fleetGroupAll,
      };
    }

    return {
      touched: false,
      fleetOnly: typeof uiConfig?.viewportFleetOnly === 'boolean' ? uiConfig?.viewportFleetOnly : false,
      type: allTypes || [],
      country: allCountries || [],
      fleetGroup: allFleetGroups || [],
      statusAll: true,
      typeAll: true,
      countryAll: true,
      sourceAll: true,
      fleetGroupAll: true,
    };
  }, [filters, allCountries, allTypes, allFleetGroups, uiConfig]);

  const handleSubmit = useCallback(
    (formData: FilterFormData) => {
      const { touched: initialTouched, ...initialValuesRaw } = initialValues;
      const { touched: formTouched, ...formDataRaw } = formData;

      const isEq =
        formDataRaw.countryAll === initialValuesRaw.countryAll &&
        formDataRaw.fleetGroupAll === initialValuesRaw.fleetGroupAll &&
        formDataRaw.fleetOnly === initialValuesRaw.fleetOnly &&
        formDataRaw.typeAll === initialValuesRaw.typeAll;

      const { fleetOnly, type, country, fleetGroup, typeAll, countryAll, fleetGroupAll } = formData;

      handleFilters({
        touched: !isEq,
        fleetOnly,
        type: type.map((e) => Number(e)),
        country: country.map((e) => Number(e)),
        fleetGroup,
        typeAll,
        countryAll,
        fleetGroupAll,
      });
      onClose();
    },
    [initialFormValue, filters],
  );
  const formik = useFormik<FilterFormData>({
    initialValues: initialFormValue,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const handleReset = useCallback(() => {
    formik.setValues({
      touched: false,
      fleetOnly: typeof uiConfig?.viewportFleetOnly === 'boolean' ? uiConfig?.viewportFleetOnly : false,
      type: allTypes || [],
      country: allCountries || [],
      fleetGroup: allFleetGroups || [],
      typeAll: true,
      countryAll: true,
      fleetGroupAll: true,
    });
  }, [allTypes, allCountries, allFleetGroups, uiConfig]);

  const checkboxGroupFleetGroup = useMemo(() => {
    return data?.filter?.fleetGroup?.values?.map((fleetGroup) => {
      return (
        <CheckboxOption<FilterFormData>
          disabled={!formik.values.fleetOnly}
          key={fleetGroup.key}
          value={fleetGroup}
          name="fleetGroup"
          formValues={formik.values}
          formSetField={formik.setFieldValue}
        />
      );
    });
  }, [data, allFleetGroups, formik]);

  const checkboxGroupType = useMemo(() => {
    return data?.filter?.type?.values?.map((typeValue) => {
      return (
        <CheckboxOption<FilterFormData>
          key={typeValue.name}
          value={typeValue}
          name="type"
          formValues={formik.values}
          formSetField={formik.setFieldValue}
        />
      );
    });
  }, [data, allTypes, formik]);
  const checkboxGroupCountry = useMemo(() => {
    return countryList?.map((countryValue) => {
      return (
        <CheckboxOption<FilterFormData>
          key={countryValue.name}
          value={countryValue}
          name="country"
          formValues={formik.values}
          formSetField={formik.setFieldValue}
        />
      );
    });
  }, [data, allCountries, formik, countryList]);

  if (isError && 'originalStatus' in error && error.originalStatus === 400) {
    filterOptionsRequest.refetch();
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    const inputValue: string = e.target.value.toLowerCase();

    const filtered = data?.filter?.country?.values?.filter((value) => value?.name?.toLowerCase().includes(inputValue));
    setContryList(filtered);
  };

  return (
    <div className={styles.root}>
      <div className={styles.dialog_modal} ref={innerRef}>
        <div className={styles.panel_header}>
          <p className={styles.heading}>{t('FilterPanelHeading')}</p>
          <div onClick={onClose} className={styles.close_button}>
            <Icon size="16" name="close-16" />
          </div>
        </div>
        <div className={styles.form_container}>
          {!data && <div className={styles.loader} />}

          {data && (
            <form onSubmit={formik.handleSubmit} className={styles.form}>
              <div className={styles.container}>
                <div className={styles.checkboxGroup}>
                  <div>
                    <label className={styles.groupName} htmlFor="fleetGroup">
                      {`${t('FilterPanelLabelFleetGroup')}:`}
                    </label>

                    <label className={styles.checkboxLabel} aria-disabled={uiConfig?.viewportFleetOnly}>
                      <input
                        id="fleetOnly"
                        type="checkbox"
                        name="fleetOnly"
                        className={styles.checkboxInput}
                        onChange={formik.handleChange}
                        checked={formik.values.fleetOnly}
                        disabled={uiConfig?.viewportFleetOnly}
                      />
                      {t('FilterPanelLabelFleetOnly')}
                    </label>

                    <CheckboxAll<FilterFormData>
                      disabled={!formik.values.fleetOnly}
                      name="fleetGroup"
                      allValues={allFleetGroups}
                      formValues={formik.values}
                      formHandleChange={formik.handleChange}
                      formSetField={formik.setFieldValue}
                    />
                    <div className={styles.listGroup}>{checkboxGroupFleetGroup}</div>
                  </div>
                </div>

                <div className={clsx(styles.checkboxGroup, styles.countryFilter)}>
                  <label className={styles.groupName} htmlFor="country">
                    {`${t('Flag')}:`}
                  </label>

                  <CheckboxAll<FilterFormData>
                    name="country"
                    allValues={allCountries}
                    formValues={formik.values}
                    formHandleChange={formik.handleChange}
                    formSetField={formik.setFieldValue}
                  />
                  <Input
                    type="search"
                    onChange={handleInputChange}
                    placeholder={t('SearchPlaceholder')}
                    className={styles.input}
                    value={inputValue}
                  />
                  <div className={styles.listGroup}>{checkboxGroupCountry}</div>
                </div>

                <div className={styles.checkboxGroup}>
                  <label className={styles.groupName} htmlFor="type">
                    {`${t('ShipTypesLabel')}:`}
                  </label>

                  <CheckboxAll<FilterFormData>
                    name="type"
                    allValues={allTypes}
                    formValues={formik.values}
                    formHandleChange={formik.handleChange}
                    formSetField={formik.setFieldValue}
                  />
                  <div className={styles.listGroup}>{checkboxGroupType}</div>
                </div>
              </div>

              <div className={styles.form_actions}>
                <Button
                  className={styles.action_button}
                  type="button"
                  label={t('FilterPanelButtonReset')}
                  variant="general"
                  onClick={handleReset}
                />
                <div>
                  <Button className={styles.action_button} label={t('FilterPanelButtonSubmit')} variant="general" type="submit" />
                  <Button
                    className={styles.action_button}
                    type="button"
                    label={t('ButtonLabelDiscard')}
                    variant="general"
                    onClick={onClose}
                  />
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
